import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { MessageSquare, Pin, Search, SlidersHorizontal, X } from 'lucide-react'
import { toast } from 'react-toastify'
import { openAIUrl } from 'urls'
import { getAxiosDefaultConfig } from '../../../modules/utils'
import { selectRootUser } from '../../../rootSlice'
import {
  fetchMoreOpenAIChatHistory,
  fetchOpenAIChatConversation,
  fetchOpenAIChatHistory,
  selectOpenAIChatHistory,
  selectOpenAIChatHistoryHasMore,
  selectOpenAIChatHistoryLoadingMore,
  selectOpenAIChatHistoryOffset,
  selectOpenAIChatHistoryStatus,
  selectActiveChatIds,
  setSelectedTaskId,
  togglePinOpenAIChatHistory,
} from '../../../features/openai/openaiSlice'
import { getChatHistoryListPreview } from '../../../features/openai/chatHistoryPreview'

const SEARCH_PAGE_SIZE = 20
const SEARCH_DEBOUNCE_MS = 250

type ChatHistoryRowProps = {
  row: ChatHistoryRow
  activeChatIds: string[]
  onOpen: (row: ChatHistoryRow) => void
  onTogglePin: (e: React.MouseEvent, row: ChatHistoryRow) => void
}

function ChatRow({ row, activeChatIds, onOpen, onTogglePin }: ChatHistoryRowProps) {
  const title = getRowTitle(row)
  const preview = getRowPreview(row)
  const isChatLoading = activeChatIds.includes(row.message_id)
  const isPinned = Boolean(row.pinned_at)
  return (
    <div
      className="tw-group tw-flex tw-w-full tw-items-center tw-gap-4 tw-py-3 tw-border-b tw-border-slate-100 tw-cursor-pointer hover:tw-bg-slate-50 tw-transition-colors"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(row)}
      onKeyDown={(e) => e.key === 'Enter' && onOpen(row)}
    >
      {isChatLoading ? (
        <span className="fa fa-spinner fa-spin tw-ml-1 tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-slate-400" title="In progress" />
      ) : (
        <MessageSquare className="tw-ml-1 tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-slate-400 group-hover:tw-text-slate-600" />
      )}
      <span className="tw-min-w-0 tw-flex-1">
        <span className="tw-block tw-truncate tw-text-sm tw-font-medium tw-text-slate-800">{title}</span>
        {preview && preview !== title && (
          <span className="tw-mt-0.5 tw-block tw-truncate tw-text-xs tw-text-slate-500">{preview}</span>
        )}
      </span>
      <span className="tw-w-16 tw-flex-shrink-0 tw-text-right tw-text-xs tw-text-slate-400">{getRowDate(row)}</span>
      <button
        type="button"
        onClick={(e) => onTogglePin(e, row)}
        title={isPinned ? 'Unpin' : 'Pin'}
        className={`tw-flex-shrink-0 tw-rounded tw-border-0 tw-bg-transparent tw-p-1 tw-transition-colors ${
          isPinned
            ? 'tw-text-blue-500 hover:tw-text-blue-700'
            : 'tw-text-slate-300 tw-opacity-0 group-hover:tw-opacity-100 hover:tw-text-slate-600'
        }`}
      >
        <Pin className="tw-h-3.5 tw-w-3.5" />
      </button>
    </div>
  )
}

type ChatRowListProps = {
  rows: ChatHistoryRow[]
  isSearching: boolean
  isInitialLoading: boolean
  activeChatIds: string[]
  emptyMessage: string
  onOpen: (row: ChatHistoryRow) => void
  onTogglePin: (e: React.MouseEvent, row: ChatHistoryRow) => void
}

function ChatRowList({ rows, isSearching, isInitialLoading, activeChatIds, emptyMessage, onOpen, onTogglePin }: ChatRowListProps) {
  const { pinnedRows, recentRows } = useMemo(() => {
    if (isSearching) return { pinnedRows: [], recentRows: rows }
    return rows.reduce(
      (acc: { pinnedRows: ChatHistoryRow[]; recentRows: ChatHistoryRow[] }, row) => {
        if (row.pinned_at) acc.pinnedRows.push(row)
        else acc.recentRows.push(row)
        return acc
      },
      { pinnedRows: [], recentRows: [] }
    )
  }, [rows, isSearching])

  if (isInitialLoading) {
    return (
      <div className="tw-py-16 tw-text-center tw-text-sm tw-text-slate-500">
        <span className="fa fa-spinner fa-spin tw-mr-2" />
        Loading chats...
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div className="tw-py-16 tw-text-center">
        <MessageSquare className="tw-mx-auto tw-mb-3 tw-h-8 tw-w-8 tw-text-slate-300" />
        <p className="tw-m-0 tw-text-sm tw-font-medium tw-text-slate-700">{emptyMessage}</p>
        <p className="tw-mt-1 tw-text-xs tw-text-slate-500">
          {isSearching ? 'Try a different search term.' : 'Start a new chat to build your history.'}
        </p>
      </div>
    )
  }

  return (
    <div>
      {pinnedRows.length > 0 && (
        <div>
          <p className="tw-mb-1 tw-mt-2 tw-px-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-slate-400">
            Pinned
          </p>
          {pinnedRows.map((row) => (
            <ChatRow key={row.message_id} row={row} activeChatIds={activeChatIds} onOpen={onOpen} onTogglePin={onTogglePin} />
          ))}
        </div>
      )}
      {recentRows.length > 0 && (
        <div>
          {pinnedRows.length > 0 && (
            <p className="tw-mb-1 tw-mt-4 tw-px-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-slate-400">
              Recent
            </p>
          )}
          {recentRows.map((row) => (
            <ChatRow key={row.message_id} row={row} activeChatIds={activeChatIds} onOpen={onOpen} onTogglePin={onTogglePin} />
          ))}
        </div>
      )}
    </div>
  )
}

type ChatVisibilityFilter = 'all' | 'private' | 'shared'

type ChatHistoryRow = {
  message_id: string
  app_email?: string
  message?: string | null
  title?: string | null
  preview?: string | null
  matched_turns?: Array<{ excerpt?: string }>
  create_time?: string | number | null
  created_at?: string | number | null
  updated_at?: string | number | null
  is_private?: boolean | null
  pinned_at?: string | null
  [key: string]: any
}

function normaliseSearchItems(items: any[]): ChatHistoryRow[] {
  return items.map((item) => ({
    ...item,
    created_at: item.created_at || item.create_time || item.updated_at || '',
  }))
}

function stripPreviewMarkup(value: string): string {
  return value
    .replace(/#{1,6}\s*/g, '')
    .replace(/\*\*|__|\*|_/g, '')
    .replace(/`{1,3}[^`]*`{1,3}/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()
}

function getRowTitle(row: ChatHistoryRow): string {
  const title = row.title || getChatHistoryListPreview(row)
  return stripPreviewMarkup(title || '') || 'Untitled'
}

function getRowPreview(row: ChatHistoryRow): string {
  const preview = row.preview || row.matched_turns?.[0]?.excerpt || ''
  return stripPreviewMarkup(preview).slice(0, 180)
}

function formatChatDate(raw: string | number | null | undefined): string {
  if (raw === undefined || raw === null || raw === '') return ''

  const parsed = typeof raw === 'number'
    ? new Date(raw < 1e12 ? raw * 1000 : raw)
    : new Date(raw)

  if (Number.isNaN(parsed.getTime())) return ''

  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const dateStart = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()).getTime()
  const diffDays = Math.round((todayStart - dateStart) / 86400000)

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays > 1 && diffDays < 7) {
    return parsed.toLocaleDateString('en-US', { weekday: 'short' })
  }
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function getRowDate(row: ChatHistoryRow): string {
  return formatChatDate(row.updated_at || row.created_at || row.create_time)
}

const filterOptions: Array<{ value: ChatVisibilityFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'private', label: 'Private' },
  { value: 'shared', label: 'Shared' },
]

const ChatHistoryMainView: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user = useSelector(selectRootUser)
  const chatHistory = useSelector(selectOpenAIChatHistory)
  const chatHistoryStatus = useSelector(selectOpenAIChatHistoryStatus)
  const chatHistoryHasMore = useSelector(selectOpenAIChatHistoryHasMore)
  const chatHistoryLoadingMore = useSelector(selectOpenAIChatHistoryLoadingMore)
  const chatHistoryOffset = useSelector(selectOpenAIChatHistoryOffset)
  const activeChatIds = useSelector(selectActiveChatIds)

  const [searchInput, setSearchInput] = useState('')
  const [searchItems, setSearchItems] = useState<ChatHistoryRow[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchLoadingMore, setSearchLoadingMore] = useState(false)
  const [searchHasMore, setSearchHasMore] = useState(false)
  const [searchOffset, setSearchOffset] = useState(0)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [visibilityFilter, setVisibilityFilter] = useState<ChatVisibilityFilter>('all')
  const searchAbortRef = useRef<AbortController | null>(null)

  const trimmedSearch = searchInput.trim()
  const isSearching = trimmedSearch.length > 0

  useEffect(() => {
    if (chatHistoryStatus === 'idle' && user?.email) {
      dispatch(fetchOpenAIChatHistory({ app_email: user.email }) as any)
    }
  }, [chatHistoryStatus, dispatch, user?.email])

  const resetSearchState = useCallback((clearInput = false) => {
    searchAbortRef.current?.abort()
    searchAbortRef.current = null
    if (clearInput) setSearchInput('')
    setSearchItems([])
    setSearchLoading(false)
    setSearchLoadingMore(false)
    setSearchHasMore(false)
    setSearchOffset(0)
    setSearchError(null)
  }, [])

  const runSearch = useCallback(async (query: string, nextOffset = 0, append = false) => {
    if (!user?.email || !query) return

    const controller = new AbortController()
    searchAbortRef.current?.abort()
    searchAbortRef.current = controller
    setSearchError(null)

    if (append) {
      setSearchLoadingMore(true)
    } else {
      setSearchLoading(true)
      setSearchLoadingMore(false)
    }

    try {
      const params = new URLSearchParams({
        q: query,
        limit: String(SEARCH_PAGE_SIZE),
        offset: String(nextOffset),
      })
      const res = await axios.get(
        `${openAIUrl}/chat/history/${encodeURIComponent(user.email)}/search?${params.toString()}`,
        { ...getAxiosDefaultConfig(), signal: controller.signal }
      )
      if (controller.signal.aborted) return

      const nextItems = normaliseSearchItems(Array.isArray(res?.data?.items) ? res.data.items : [])
      if (append) {
        setSearchItems((prev) => {
          const existingIds = new Set(prev.map((item) => item.message_id))
          return [...prev, ...nextItems.filter((item) => !existingIds.has(item.message_id))]
        })
      } else {
        setSearchItems(nextItems)
      }
      setSearchHasMore(Boolean(res?.data?.hasMore))
      setSearchOffset(nextOffset + nextItems.length)
    } catch (error: any) {
      if (axios.isCancel(error) || error?.name === 'CanceledError' || error?.name === 'AbortError') return
      console.error('[ChatHistoryMainView] Search failed:', error)
      setSearchError('Search failed. Try again.')
      if (!append) {
        setSearchItems([])
        setSearchHasMore(false)
        setSearchOffset(0)
      }
    } finally {
      if (searchAbortRef.current === controller) {
        searchAbortRef.current = null
      }
      setSearchLoading(false)
      setSearchLoadingMore(false)
    }
  }, [user?.email])

  useEffect(() => {
    if (!trimmedSearch) {
      resetSearchState(false)
      return
    }

    const timeoutId = window.setTimeout(() => {
      void runSearch(trimmedSearch, 0, false)
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timeoutId)
      searchAbortRef.current?.abort()
    }
  }, [resetSearchState, runSearch, trimmedSearch])

  useEffect(() => () => {
    searchAbortRef.current?.abort()
  }, [])

  const baseItems = useMemo(() => {
    const rows = isSearching ? searchItems : chatHistory
    return rows.filter((row: ChatHistoryRow) => row?.app_email === undefined || row.app_email === user?.email)
  }, [chatHistory, isSearching, searchItems, user?.email])

  const displayedItems = useMemo(() => {
    return baseItems.filter((row: ChatHistoryRow) => {
      if (visibilityFilter === 'private') return !!row.is_private
      if (visibilityFilter === 'shared') return !row.is_private
      return true
    })
  }, [baseItems, visibilityFilter])

  const isInitialLoading = isSearching
    ? searchLoading && searchItems.length === 0
    : chatHistoryStatus === 'loading' && chatHistory.length === 0
  const isLoadingMore = isSearching ? searchLoadingMore : chatHistoryLoadingMore
  const canLoadMore = isSearching ? searchHasMore : chatHistoryHasMore

  const handleShowMore = async () => {
    if (!user?.email || isLoadingMore) return
    if (isSearching) {
      await runSearch(trimmedSearch, searchOffset, true)
      return
    }
    dispatch(fetchMoreOpenAIChatHistory({ app_email: user.email, offset: chatHistoryOffset }) as any)
  }

  const handleOpenChat = async (row: ChatHistoryRow) => {
    if (!user?.email || !row?.message_id) return

    try {
      dispatch(setSelectedTaskId(null) as any)
      const selectedChat = row.message
        ? row
        : await dispatch(fetchOpenAIChatConversation({
          app_email: user.email,
          message_id: row.message_id,
        }) as any).unwrap()

      navigate('/ai', { state: { selectedChat } })
    } catch (error) {
      console.error('[ChatHistoryMainView] Failed to open chat:', error)
      toast.error('Failed to open chat')
    }
  }

  const handleNewChat = () => {
    dispatch(setSelectedTaskId(null) as any)
    navigate('/ai', { state: { action: 'new-chat' } })
  }

  const handleTogglePin = async (e: React.MouseEvent, row: ChatHistoryRow) => {
    e.stopPropagation()
    if (!user?.email) return
    try {
      await (dispatch as any)(togglePinOpenAIChatHistory({
        app_email: user.email,
        message_id: row.message_id,
        pinned: !row.pinned_at,
      })).unwrap()
    } catch (error) {
      console.error('Failed to update pin:', error)
      toast.error('Failed to update pin')
    }
  }

  const emptyMessage = isSearching
    ? `No chats match "${trimmedSearch}"`
    : visibilityFilter === 'private'
      ? 'No private chats yet'
      : visibilityFilter === 'shared'
        ? 'No shared chats yet'
        : 'No chat history yet'

  return (
    <div className="tw-flex tw-flex-1 tw-min-h-0 tw-overflow-auto tw-bg-white">
      <div className="tw-w-full tw-max-w-5xl tw-mx-auto tw-px-6 tw-py-8">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-mb-8">
          <h1 className="tw-text-2xl tw-font-semibold tw-text-slate-900 tw-m-0">Chats</h1>
          <div className="tw-flex tw-items-center tw-gap-2">
            <div className="tw-hidden sm:tw-flex tw-items-center tw-gap-1 tw-rounded-lg tw-bg-slate-50 tw-p-1">
              {filterOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setVisibilityFilter(option.value)}
                  className={`tw-rounded-md tw-border-0 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-transition-colors ${
                    visibilityFilter === option.value
                      ? 'tw-bg-white tw-text-slate-900 tw-shadow-sm'
                      : 'tw-bg-transparent tw-text-slate-500 hover:tw-text-slate-800'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="tw-flex sm:tw-hidden tw-items-center tw-gap-1 tw-text-slate-400">
              <SlidersHorizontal className="tw-h-4 tw-w-4" />
              <select
                value={visibilityFilter}
                onChange={(e) => setVisibilityFilter(e.target.value as ChatVisibilityFilter)}
                className="tw-h-8 tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-px-2 tw-text-xs tw-text-slate-700"
              >
                {filterOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={handleNewChat}
              className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-700 tw-shadow-sm hover:tw-bg-slate-50 tw-transition-colors"
            >
              New chat
            </button>
          </div>
        </div>

        <div className="tw-relative tw-mb-5">
          <Search className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search chats..."
            className="tw-h-10 tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-pl-9 tw-pr-10 tw-text-sm tw-text-slate-900 tw-outline-none tw-transition focus:tw-border-blue-300 focus:tw-bg-white focus:tw-ring-2 focus:tw-ring-blue-100"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => resetSearchState(true)}
              className="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-rounded-md tw-border-0 tw-bg-transparent tw-p-1 tw-text-slate-400 hover:tw-bg-slate-100 hover:tw-text-slate-700"
              aria-label="Clear chat search"
            >
              <X className="tw-h-4 tw-w-4" />
            </button>
          )}
        </div>

        {searchError && (
          <div className="tw-mb-4 tw-rounded-lg tw-border tw-border-red-100 tw-bg-red-50 tw-px-3 tw-py-2 tw-text-sm tw-text-red-700">
            {searchError}
          </div>
        )}

        <ChatRowList
          rows={displayedItems}
          isSearching={isSearching}
          isInitialLoading={isInitialLoading}
          activeChatIds={activeChatIds}
          emptyMessage={emptyMessage}
          onOpen={handleOpenChat}
          onTogglePin={handleTogglePin}
        />

        {canLoadMore && !isInitialLoading && (
          <div className="tw-flex tw-justify-center tw-pt-8">
            <button
              type="button"
              onClick={() => void handleShowMore()}
              disabled={isLoadingMore}
              className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-600 tw-shadow-sm hover:tw-bg-slate-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
            >
              {isLoadingMore ? (
                <>
                  <span className="fa fa-spinner fa-spin tw-mr-2" />
                  Loading...
                </>
              ) : (
                'Show more'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default ChatHistoryMainView
